import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Title from "../common/Title";
import { FaServer, FaPlus } from "react-icons/fa";
import { runbookSystemActions } from "../../actions/runbookSystem.actions";
import { MdDelete, MdEdit, MdVisibility } from "react-icons/md";
import ConfirmModal from "../common/ConfirmModal";
import { useToast } from "../../contexts/ToastContext";
import { ARMANINO_FALLBACK_ICON, SYSTEM_ICON_CANDIDATES } from "./systemIconCandidates";
import "./RunbookSystemGrid.css";

function RunbookSystemGrid({ onEditSystem, onAddSystem, onViewSystem, hideTitle }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const runbookSystem = useSelector((state) => state.runbookSystem);
  const [confirmDeleteModalShow, setConfirmDeleteModalShow] = useState(false);
  const [deleteSystemName, setDeleteSystemName] = useState("");
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // { x, y }
  const [brokenIcons, setBrokenIcons] = useState(() => new Set()); // systems whose iconUrl failed to load

  const refresh = () => {
    dispatch(runbookSystemActions.getAll());
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, []);

  // Refresh the list after a create/update (e.g. from the Add/Edit window) or delete completes.
  useEffect(() => {
    if (runbookSystem.success) {
      refresh();
    }
    // eslint-disable-next-line
  }, [runbookSystem.success]);

  useEffect(() => {
    if (runbookSystem.deleteSuccess) {
      refresh();
      setSelectedSystem(null);
      showToast("System deleted successfully", "success");
    }
    // eslint-disable-next-line
  }, [runbookSystem.deleteSuccess]);

  const handleDeleteSystem = () => {
    setConfirmDeleteModalShow(false);
    let requestParameters = {
      system: deleteSystemName,
    };

    dispatch(runbookSystemActions.remove(requestParameters));
  };

  const handleEditSelected = () => {
    if (!selectedSystem) return;
    if (onEditSystem) {
      onEditSystem(selectedSystem.system);
    } else {
      navigate(`edit?system=${selectedSystem.system}`);
    }
  };

  const handleViewSelected = () => {
    if (!selectedSystem || !onViewSystem) return;
    onViewSystem(selectedSystem.system);
  };

  const handleDeleteSelected = () => {
    if (!selectedSystem) return;
    setDeleteSystemName(selectedSystem.system);
    setConfirmDeleteModalShow(true);
  };

  const handleTileContextMenu = (e, system) => {
    e.preventDefault();
    setSelectedSystem(system);
    // Keep the menu on-screen even when right-clicking near the window's edge.
    const menuWidth = 180;
    const menuHeight = 140;
    setContextMenu({
      x: Math.min(e.clientX, window.innerWidth - menuWidth - 8),
      y: Math.min(e.clientY, window.innerHeight - menuHeight - 8),
    });
  };

  // Dismiss the context menu on the next click/scroll/escape anywhere.
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    const closeOnEscape = (e) => e.key === "Escape" && close();
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [contextMenu]);

  const items = runbookSystem.response || [];
  const isInitialLoading = runbookSystem.loading && items.length === 0;

  return (
    <div className={hideTitle ? "runbook-system-grid-root" : undefined}>
      {!hideTitle && <Title text="Runbook Systems" icon={<FaServer className="mb-1" />} />}

      {selectedSystem && (
        <div className="mb-2">
          <small className="text-muted">
            Selected: <strong>{selectedSystem.system}</strong>
          </small>
        </div>
      )}

      <div className={`system-icon-grid${hideTitle ? " fill-parent" : ""}`}>
        <button
          type="button"
          className="system-icon-tile"
          onClick={() => (onAddSystem ? onAddSystem() : navigate("add"))}
        >
          <span className="system-icon-glyph system-icon-glyph-add">
            <FaPlus />
          </span>
          <span className="system-icon-label">Add System</span>
        </button>

        {items.map((s) => {
          const effectiveIconUrl = s.iconUrl || SYSTEM_ICON_CANDIDATES[s.system] || ARMANINO_FALLBACK_ICON;
          const useImageIcon = !!effectiveIconUrl && !brokenIcons.has(s.system);
          return (
            <button
              key={s.system}
              type="button"
              className={`system-icon-tile${selectedSystem?.system === s.system ? " selected" : ""}`}
              onClick={() => setSelectedSystem(s)}
              onDoubleClick={() => onViewSystem && onViewSystem(s.system)}
              onContextMenu={(e) => handleTileContextMenu(e, s)}
            >
              <span className={`system-icon-glyph${useImageIcon ? " system-icon-glyph-image-wrap" : ""}`}>
                {useImageIcon ? (
                  <img
                    src={effectiveIconUrl}
                    alt=""
                    className="system-icon-glyph-image"
                    onError={() => setBrokenIcons((prev) => new Set(prev).add(s.system))}
                  />
                ) : (
                  <FaServer />
                )}
              </span>
              <span className="system-icon-label">{s.system}</span>
            </button>
          );
        })}
        {isInitialLoading &&
          Array.from({ length: 8 }).map((_, i) => (
            <div key={`skeleton-${i}`} className="system-icon-tile">
              <span className="system-icon-glyph skeleton" />
              <span className="system-icon-label skeleton">placeholder</span>
            </div>
          ))}
      </div>

      {contextMenu &&
        createPortal(
          <div className="system-context-menu" style={{ top: contextMenu.y, left: contextMenu.x }}>
            <button type="button" className="system-context-menu-item" onClick={handleEditSelected}>
              <MdEdit />
              Edit
            </button>
            <button
              type="button"
              className="system-context-menu-item"
              disabled={!onViewSystem}
              onClick={handleViewSelected}
            >
              <MdVisibility />
              View
            </button>
            <div className="system-context-menu-divider" />
            <button
              type="button"
              className="system-context-menu-item text-danger"
              onClick={handleDeleteSelected}
            >
              <MdDelete />
              Delete
            </button>
          </div>,
          document.body
        )}

      <ConfirmModal
        show={confirmDeleteModalShow}
        title="Delete Runbook System"
        question={`Are you sure you want to delete the system "${deleteSystemName}"?`}
        noButtonText="Cancel"
        yesButtonText="Yes, Delete"
        handleClose={() => setConfirmDeleteModalShow(false)}
        handleConfirm={handleDeleteSystem}
      />
    </div>
  );
}

export default RunbookSystemGrid;
